import { useEffect, useState } from "react";
import { Grid, Divider, useTheme, Typography, Box } from "@mui/material";
import { WhiteBox } from "@foodpilot/foods";

import ScoreSection from "./components/ScoreSection";
import TrajectorySection from "./components/TrajectorySection";
import GoalSection from "./components/GoalSection";
import Legend from "./components/Legend";
import SectionDivider from "./components/SectionDivider";

import { useActionPlanData } from "./hooks/useActionPlanData";

export type ActionPlanProps = {
    actionPlanId?: string;
    scoreId?: number | null;
    variationId?: number | null;
    customGoalValue?: number;
    customGoalYear?: number;
    customGoalUnit?: string;
    showScoreSection?: boolean;
    showTrajectorySection?: boolean;
    showGoalSection?: boolean;
    showLegend?: boolean;
    actionPlanTitleVisible?: boolean;
};

const ActionPlan = (props: ActionPlanProps) => {
    const [isInPuckEditor, setIsInPuckEditor] = useState(false);
    const theme = useTheme();

    useEffect(() => {
        const isPuckIframe = document.getElementById("preview-frame") !== null;
        setIsInPuckEditor(isPuckIframe);
    }, []);

    const {
        actionPlanId,
        scoreId,
        variationId,
        customGoalValue,
        customGoalYear,
        customGoalUnit,
        showScoreSection = true,
        showTrajectorySection = true,
        showGoalSection = true,
        showLegend = true,
        actionPlanTitleVisible,
    } = props;

    const planData = useActionPlanData({
        actionPlanId,
        scoreId,
        variationId,
        customGoalValue,
        customGoalYear,
        customGoalUnit,
    });

    const {
        isLoading,
        precision,
        scoreTitle,
        diagnosticScore,
        diagnosticScoreChart,
        diagnosticSSQCompletions,
        diagnosticCTAURL,
        projectedScore,
        projectedScoreChart,
        projectedYearValue,
        actionCount,
        projectedCTAURL,
        goalValue,
        goalYear,
        goalUnit,
        unit,
        diagnosticToProjectedDifference,
        projectedToGoalDifference,
        diagnosticToGoalDifference,
        legendItems,
    } = planData;

    const visibleSections = [showScoreSection, showTrajectorySection, showGoalSection].filter(Boolean).length;
    const columnWidth = visibleSections > 1 ? 12 / visibleSections : 12;
    const [isDiagonsticEnabled, isTrajectoryEnabled] = [
        showScoreSection && diagnosticScoreChart?.values.length > 0,
        showTrajectorySection && projectedScoreChart?.values.length > 0,
    ];

    return (
        <Box sx={{ mb: theme.spacing(2) }}>
            {actionPlanTitleVisible && scoreTitle ?
                <Box sx={{ mb: theme.spacing(4) }}>
                    <Typography variant="h2">{scoreTitle}</Typography>
                </Box>
            :   <></>}
            <WhiteBox>
                <Grid container sx={{ position: "relative" }}>
                    {showScoreSection && (
                        <Grid item xs={12} lg={columnWidth} sx={{ position: "relative", width: "100%" }}>
                            <ScoreSection
                                score={diagnosticScore}
                                chartData={diagnosticScoreChart}
                                completionPercentage={diagnosticSSQCompletions}
                                enabled={isDiagonsticEnabled}
                                unit={unit}
                                isLoading={!isInPuckEditor && isLoading}
                                precision={precision}
                                ctaURL={diagnosticCTAURL}
                            />
                            <SectionDivider
                                showDivider={visibleSections > 1}
                                showPercentage={isTrajectoryEnabled || (!showTrajectorySection && isDiagonsticEnabled)}
                                percentageDifference={
                                    showTrajectorySection ? diagnosticToProjectedDifference : diagnosticToGoalDifference
                                }
                                position="right"
                            />
                        </Grid>
                    )}

                    {showTrajectorySection && (
                        <Grid item xs={12} lg={columnWidth} sx={{ position: "relative" }}>
                            <TrajectorySection
                                score={projectedScore}
                                chartData={projectedScoreChart}
                                year={projectedYearValue}
                                actionsTaken={actionCount}
                                enabled={isTrajectoryEnabled}
                                unit={unit}
                                precision={precision}
                                ctaURL={projectedCTAURL}
                                isLoading={!isInPuckEditor && isLoading}
                            />
                            <SectionDivider
                                showDivider={showGoalSection && visibleSections > 1}
                                showPercentage={isTrajectoryEnabled}
                                percentageDifference={projectedToGoalDifference}
                                position="right"
                            />
                        </Grid>
                    )}

                    {showGoalSection && (
                        <Grid item xs={12} lg={columnWidth} sx={{ position: "relative" }}>
                            <GoalSection
                                score={Number(goalValue.toFixed(2))}
                                year={goalYear}
                                unitLabel={goalUnit}
                                precision={precision}
                            />
                        </Grid>
                    )}
                </Grid>

                {showLegend && legendItems && legendItems.length > 0 && (
                    <>
                        <Divider />
                        <Legend items={legendItems} />
                    </>
                )}
            </WhiteBox>
        </Box>
    );
};

export default ActionPlan;
